import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FlaskConical,
  Users,
  TrendingUp,
  AlertTriangle,
  Plus,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  BarChart3,
  Eye,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/store';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

interface Stats {
  total_studies: number;
  active_studies: number;
  total_participants: number;
  completion_rate: number;
  no_show_rate: number;
}

interface StudyPreview {
  id: string;
  title: string;
  status: string;
  max_participants: number;
  current_participants: number;
  compensation_amount: number;
  created_at: string;
  category: string | null;
}

export default function ResearcherDashboard() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentStudies, setRecentStudies] = useState<StudyPreview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [statsData, studiesData] = await Promise.all([
          api.getResearcherStats(),
          api.getMyStudies({ per_page: 5 }),
        ]);
        if (!mounted) return;
        setStats(statsData);
        setRecentStudies(studiesData.data ?? []);
      } catch {
        // silently fail
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-[3px] border-gray-200 dark:border-gray-800 border-t-brand-500 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const completionPct = stats?.completion_rate ?? 0;
  const noShowPct = stats?.no_show_rate ?? 0;

  return (
    <motion.div initial="hidden" animate="visible" className="space-y-6">
      {/* Greeting */}
      <motion.div variants={fadeUp} custom={0} className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="page-header">
            Welcome back, {user?.first_name}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage your studies and track participant engagement
          </p>
        </div>
        <Link to="/create-study" className="btn-primary">
          <Plus className="w-4 h-4" />
          Create Study
        </Link>
      </motion.div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div variants={fadeUp} custom={1} className="stat-card">
          <div className="w-8 h-8 rounded-lg bg-brand-50 dark:bg-brand-900/20 flex items-center justify-center mb-1">
            <FlaskConical className="w-4 h-4 text-brand-500" />
          </div>
          <span className="text-2xl font-bold">{stats?.total_studies ?? 0}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Total studies</span>
        </motion.div>

        <motion.div variants={fadeUp} custom={2} className="stat-card">
          <div className="w-8 h-8 rounded-lg bg-success-50 dark:bg-green-900/20 flex items-center justify-center mb-1">
            <Eye className="w-4 h-4 text-success-500" />
          </div>
          <span className="text-2xl font-bold">{stats?.active_studies ?? 0}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Active studies</span>
        </motion.div>

        <motion.div variants={fadeUp} custom={3} className="stat-card">
          <div className="w-8 h-8 rounded-lg bg-accent-50 dark:bg-accent-900/20 flex items-center justify-center mb-1">
            <Users className="w-4 h-4 text-accent-500" />
          </div>
          <span className="text-2xl font-bold">{stats?.total_participants ?? 0}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Total participants</span>
        </motion.div>

        <motion.div variants={fadeUp} custom={4} className="stat-card">
          <div className="w-8 h-8 rounded-lg bg-warning-50 dark:bg-yellow-900/20 flex items-center justify-center mb-1">
            <TrendingUp className="w-4 h-4 text-warning-500" />
          </div>
          <span className="text-2xl font-bold">{completionPct.toFixed(0)}%</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Completion rate</span>
        </motion.div>
      </div>

      {/* Completion & No-Show Rate Bars */}
      <motion.div variants={fadeUp} custom={5} className="card p-6">
        <h2 className="section-header mb-4">Participation Metrics</h2>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-success-500" />
                <span className="text-sm font-medium">Completion Rate</span>
              </div>
              <span className="text-sm font-bold text-success-500">{completionPct.toFixed(1)}%</span>
            </div>
            <div className="w-full h-3 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-success-400 to-success-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${completionPct}%` }}
                transition={{ duration: 1, delay: 0.5, ease: 'easeOut' }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-danger-500" />
                <span className="text-sm font-medium">No-Show Rate</span>
              </div>
              <span className="text-sm font-bold text-danger-500">{noShowPct.toFixed(1)}%</span>
            </div>
            <div className="w-full h-3 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-danger-400 to-danger-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${noShowPct}%` }}
                transition={{ duration: 1, delay: 0.7, ease: 'easeOut' }}
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Recent Studies */}
      <motion.div variants={fadeUp} custom={6} className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="section-header">Recent Studies</h2>
          <Link to="/my-studies" className="text-sm font-medium text-brand-500 hover:text-brand-600 flex items-center gap-1">
            View all <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {recentStudies.length === 0 ? (
          <div className="text-center py-10">
            <FlaskConical className="w-12 h-12 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">You haven't created any studies yet</p>
            <Link to="/create-study" className="btn-primary inline-flex">
              <Plus className="w-4 h-4" />
              Create Your First Study
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-800">
                  <th className="px-6 py-3">Study</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Participants</th>
                  <th className="px-6 py-3">Compensation</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {recentStudies.map((study) => (
                  <tr key={study.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-semibold truncate max-w-[200px]">{study.title}</p>
                        {study.category && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{study.category}</p>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={study.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-500 rounded-full"
                            style={{ width: `${(study.current_participants / Math.max(study.max_participants, 1)) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {study.current_participants}/{study.max_participants}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium">${study.compensation_amount}</span>
                    </td>
                    <td className="px-6 py-4">
                      <Link
                        to={`/my-studies/${study.id}`}
                        className="text-sm font-medium text-brand-500 hover:text-brand-600 flex items-center gap-1"
                      >
                        Manage <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: 'badge bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
    active: 'badge-success',
    paused: 'badge-warning',
    completed: 'badge-brand',
    cancelled: 'badge-danger',
  };
  return <span className={styles[status] || 'badge'}>{status}</span>;
}
